import React from 'react';

export interface CardProps {
  children: React.ReactNode;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  variant?: 'spotlight' | 'glass' | 'ghost' | 'flat' | 'elevated' | 'accent';
  hover?: boolean;
  onClick?: () => void;
}

const paddingClasses = {
  none: '',
  sm: 'p-4',
  md: 'p-5',
  lg: 'p-6 lg:p-8',
};

/* Every card on the desk is a sheet of paper. The variant names survive for the
   callers that pass them; only 'ghost' (no surface) and 'accent' (manila) differ. */
const variantClasses: Record<NonNullable<CardProps['variant']>, string> = {
  spotlight: 'br-sheet',
  glass: 'br-sheet',
  ghost: 'container-ghost',
  flat: 'br-sheet',
  elevated: 'br-sheet',
  accent: 'br-sheet br-sheet--cream',
};

export const Card: React.FC<CardProps> = ({
  children,
  className = '',
  padding = 'md',
  variant = 'flat',
  hover = false,
  onClick,
}) => {
  const base = variantClasses[variant];
  const hoverClasses = hover
    ? variant === 'ghost' 
      ? 'hover:bg-black/5 transition-colors cursor-pointer'
      : 'br-sheet--hover cursor-pointer'
    : '';
  const paddingClass = paddingClasses[padding];

  return (
    <div
      className={`br-card br-card--${variant} ${base} ${hoverClasses} ${paddingClass} ${className}`}
      onClick={onClick}
      onKeyDown={onClick ? event => {
        if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault(); onClick();
        }
      } : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {children}
    </div>
  );
};
